import {
  AfterViewInit,
  Component,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { Router } from '@angular/router';
import { tap } from 'rxjs/operators';
import { AppService } from '../../app.service';
import { ApiRequestService } from '../../shared/api-request.service';
import { RBACService } from '../../shared/rbac.service';
import { UtilsService } from '../../shared/utils.service';
import { CustomDataSource } from '../../utils/custom-data-source';
import { CoursesEditComponent } from '../courses-edit/courses-edit.component';
import { CourseModel } from 'src/app/models/course.model';
import { CoursesViewComponent } from '../courses-view/courses-view.component';
import { CoursesReportComponent } from '../courses-report/courses-report.component';
import { ChartsUtilsService } from '../../charts/charts-utils.service';

@Component({
  selector: 'app-courses',
  templateUrl: './courses.component.html',
  styleUrls: ['./courses.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class CoursesComponent implements OnInit, AfterViewInit {
  // RBAC permission flags: resolved once in ngOnInit().
  canCreate: boolean = false;
  canView: boolean = false;
  canEdit: boolean = false;
  canDelete: boolean = false;

  // Build displayedColumns dynamically: NOT as a static array.
  displayedColumns: string[] = [];
  dataSource = new CoursesDataSource(this.app, this.api);

  // the paginator and sorter
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  constructor(
    public app: AppService,
    private api: ApiRequestService,
    public utils: UtilsService,
    private dialog: MatDialog,
    public chartsUtils: ChartsUtilsService,
    public rbac: RBACService,
    private router: Router
  ) {}

  ngOnInit() {
    // 1. Resolve RBAC permission flags.
    this.canCreate = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_ACCOUNT_INDUCTIONS,
          RBACService.RBAC_A.A_CREATE
        )
      ],
      true
    );
    this.canView = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_ACCOUNT_INDUCTIONS,
          RBACService.RBAC_A.A_VIEW
        )
      ],
      true
    );
    this.canEdit = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_ACCOUNT_INDUCTIONS,
          RBACService.RBAC_A.A_EDIT
        )
      ],
      true
    );
    this.canDelete = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_ACCOUNT_INDUCTIONS,
          RBACService.RBAC_A.A_DELETE
        )
      ],
      true
    );

    // 2. Page-level access: redirect if user has NO permissions at all.
    if (!this.canCreate && !this.canView && !this.canEdit && !this.canDelete) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to access account inductions.'
      );
      this.router.navigate(['/dashboard']);
      return;
    }

    // 3. Build displayedColumns dynamically.
    this.displayedColumns = [
      'id',
      'name',
      'enabled',
      'assigned_users_count',
      'date_created',
      'actions'
    ];
    if (this.canDelete) {
      this.displayedColumns.unshift('select');
    }

    // 4. Only load data if user has view permission.
    if (this.canView) {
      this.dataSource.getData();
    }
  }

  ngAfterViewInit() {
    // The table, its matSort and its mat-paginator all live inside a container gated by
    // *ngIf="canView", so without that permission Angular never creates those view
    // children and this.sort / this.paginator stay undefined. Bail out before wiring them up.
    if (!this.canView) {
      return;
    }

    // reset the paginator when sorting
    this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));

    const _tap = tap(() => {
      this.dataSource.limit = this.paginator.pageSize;
      this.dataSource.offset = this.paginator.pageIndex;
      this.dataSource.sort_by = this.sort.active;
      this.dataSource.sort_order = this.sort.direction;

      // Sort UTC columns by their corresponding date columns.
      if (this.sort.active == 'date_created_UTC') {
        this.dataSource.sort_by = 'date_created';
      }

      this.dataSource.getData();
    });

    // subscribe to the paginator tap events
    this.paginator.page.pipe(_tap).subscribe();
    this.sort.sortChange.pipe(_tap).subscribe();
  }

  onAdd() {
    if (!this.canCreate) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to create inductions.'
      );
      return;
    }
    this.openEditDialog(new CourseModel());
  }

  onEdit(course: CourseModel) {
    if (!this.canEdit) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to edit inductions.'
      );
      return;
    }
    this.openEditDialog(course);
  }

  private openEditDialog(course: CourseModel) {
    this.dialog
      .open(CoursesEditComponent, {
        width: '900px',
        data: { course: { ...course } }
      })
      .afterClosed()
      .subscribe((success) => {
        if (!success) {
          return;
        }
        if (this.canView) {
          this.dataSource.getData(true);
        }
      });
  }

  onView(course: CourseModel) {
    if (!this.canView) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to view inductions.'
      );
      return;
    }
    this.dialog
      .open(CoursesViewComponent, {
        width: '900px',
        data: course
      })
      .afterClosed()
      .subscribe((success) => {
        if (!success) {
          return;
        }
        if (this.canView) {
          this.dataSource.getData(true);
        }
      });
  }

  onRemove(id: number) {
    if (!this.canDelete) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to delete inductions.'
      );
      return;
    }
    this.utils.showModal(
      'Remove Induction',
      'Are you sure you want to remove this induction?',
      () => {
        this.api
          .makeRequest('delete', `v1/inductions/${id}`)
          .then((response: any) => {
            this.dataSource.selection.deselect(id);
            this.utils.showToast('The induction was removed.');
            this.dataSource.getData();
          });
      }
    );
  }

  onDeleteSelected() {
    if (!this.canDelete) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to delete inductions.'
      );
      return;
    }
    this.utils.showModal(
      'Remove Selected Induction',
      'Are you sure you want to remove the selected inductions?',
      () => {
        const selectedInductions = this.dataSource.selection.selected.join(',');
        this.api
          .makeRequest('delete', `v1/inductions/${selectedInductions}`)
          .then((response: any) => {
            this.dataSource.selection.clear();
            this.utils.showToast('The selected inductions was removed.');
            this.dataSource.getData();
          });
      }
    );
  }

  onViewReport(course: CourseModel) {
    if (!this.canView) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to view induction reports.'
      );
      return;
    }
    this.dialog
      .open(CoursesReportComponent, {
        width: '1024px',
        data: course
      })
      .afterClosed()
      .subscribe((success) => {
        if (!success) {
          return;
        }
        if (this.canView) {
          this.dataSource.getData(true);
        }
      });
  }
}

export class CoursesDataSource extends CustomDataSource {
  // record sorting and direction
  sort_by = 'name';
  sort_order = 'asc';

  getData(resetOffset: boolean = false) {
    this.getDataFromLaravelAPI('inductions', resetOffset);
  }
}
